import { Fragment, useEffect, useState, useCallback } from "react";
import { useIsMobile } from "../../hooks/useIsMobile";
import { Box, Button, Divider, Paper, Stack, Typography } from "@mui/material";
import API from "../../api/axiosConfig";
import { useToast } from "../../context/ToastContext";
import CircularProgress from "@mui/material/CircularProgress";

export default function PendingMeetingDisplay() {
  // Displays the pending meetings with a button to accept or decline on the profile page.

  const showToast = useToast();

  // Initialize mobile sensor
  const isMobile = useIsMobile();

  // Initialize the loading variable
  const [loading, setLoading] = useState(true);
  // Initialize the meetings variable
  const [meetings, setMeetings] = useState([]);

  function formatDateTime(date) {
    // Formats an ISO 8601 time string into a user-friendly date and time string.
    // @param {string} isoString The time string in 'YYYY-MM-DDTHH:mm:ss' format.
    // @returns {string} A formatted string like "September 18, 2025 at 5:02 PM".
    if (!date) {
      return "Time not available";
    }

    try {
      const dateObject = new Date(date);

      if (isNaN(dateObject.getTime())) {
        return "Invalid time format";
      }

      const formattedDate = dateObject.toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      });

      const formattedTime = dateObject.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });

      return `${formattedDate}, ${formattedTime}`;
    } catch (error) {
      console.error("Error formatting date:", error);
      return "Time format error";
    }
  }

  const gatherMeetings = useCallback(async () => {
    try {
      setLoading(true);
      const response = await API.post("/api/meetings/getPending", {
        filter: "pending",
      });

      // Set the fetched meetings accordingly.
      const fetchedMeetings = response.data || [];
      setMeetings(fetchedMeetings);
    } catch (err) {
      showToast("Error during meeting fetch", "error");
      // Clear meetings so the user doesn't see old data if the fetch failed
      setMeetings([]);
    } finally {
      // Set loading to false after asynchronous operation completes
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    gatherMeetings();
  }, [gatherMeetings]);

  const handleAccept = async (meetingId) => {
    try {
      await API.post("/api/meetings/accept", meetingId, {
        headers: { "Content-Type": "application/json" },
      });

      showToast("Meeting accepted!", "success");
      gatherMeetings();
    } catch (err) {
      // Bad request status.
      if (err.response?.status === 400) {
        showToast(
          "Error accepting meeting! User is NOT on pending guest list.",
          "error",
        );
        return;
      } else {
        showToast("Error accepting meeting!", "error");
      }
    }
  };

  const handleDecline = async (meetingId) => {
    try {
      await API.post("/api/meetings/decline", meetingId, {
        headers: { "Content-Type": "application/json" },
      });

      showToast("Meeting declined.", "success");
      gatherMeetings();
    } catch (err) {
      // Bad request status.
      if (err.response?.status === 400) {
        showToast(
          "Error declining meeting! User is not on the pending guest list!",
          "error",
        );
      } else {
        // Again, why no navigate or return statement here?
        showToast("Error declining meeting!");
      }
    }
  };


  if (loading) {
    <Box>
      <CircularProgress aria-label="Loading…" />
    </Box>;
  }

  if (isMobile) {
    ///////////////////
    // MOBILE RETURN //
    ///////////////////

    return (
      <Box justifyItems={"center"} alignItems={"center"}>
        <Paper variant="main">
          <Typography
            variant="h5"
            mb={2}
            justifySelf="left"
            sx={(theme) => ({
              fontWeight: "bold",
              color: theme.palette.paper.light,
            })}
          >
            Pending Meetings
          </Typography>
          {meetings.length > 0 ? (
            <Stack spacing={4}>
              {meetings.map((meeting, index) => (
                <Fragment key={meeting.id}>
                  <Box>
                    <Box width={"100%"} justifyItems="left">
                      <Typography
                        variant="h6"
                        sx={(theme) => ({
                          fontWeight: "bold",
                          mt: 1,
                          color: theme.palette.paper.light,
                        })}
                      >
                        Description:
                      </Typography>
                      <Typography variant="body1" justifySelf="left">
                        {meeting.description}
                      </Typography>
                    </Box>
                    <Box width={"100%"} justifyItems="left">
                      <Typography
                        variant="h6"
                        sx={(theme) => ({
                          fontWeight: "bold",
                          mt: 1,
                          color: theme.palette.paper.light,
                        })}
                      >
                        Time & Location:
                      </Typography>
                      <Typography variant="body1">
                        {formatDateTime(meeting.time)}
                      </Typography>
                      <Typography variant="body1" sx={{ fontStyle: "italic" }}>
                        {meeting.location}
                      </Typography>
                      <Typography variant="body1" sx={{ fontStyle: "italic" }}>
                        Meeting Creator: {meeting.parentName}
                      </Typography>
                    </Box>
                    <Stack
                      direction="row"
                      spacing={2}
                      justifyContent={"center"}
                      mt={2}
                    >
                      <Button
                        variant="contained"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAccept(meeting.id);
                        }}
                        sx={{ width: "50%", height: "96px" }}
                      >
                        Accept
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDecline(meeting.id);
                        }}
                        sx={{ width: "50%", height: "96px" }}
                      >
                        Decline
                      </Button>
                    </Stack>
                  </Box>
                  {index < meetings.length - 1 && (
                    <Divider
                      sx={(theme) => ({
                        border: `1px solid ${theme.palette.paper.light}`,
                        marginTop: 4,
                        marginBottom: 2,
                      })}
                    />
                  )}
                </Fragment>
              ))}
            </Stack>
          ) : (
            <Typography
              variant="body1"
              sx={{ textAlign: "center", fontStyle: "italic" }}
            >
              No pending meetings found.
            </Typography>
          )}
        </Paper>
      </Box>
    );
  }

  ///////////////////
  // NORMAL RETURN //
  ///////////////////

  return (
    <Box sx={{ width: "100%", boxSizing: "border-box" }}>
      <Typography
        variant="h6"
        fontWeight="700"
        sx={(theme) => ({
          mb: 3,
          textAlign: "left",
          color: theme.palette.paper.light,
          borderBottom: "1px solid",
          borderColor: "divider",
          pb: 1,
        })}
      >
        Pending Meetings
      </Typography>

      {meetings.length > 0 ? (
        <Stack spacing={3} sx={{ width: "100%" }}>
          {meetings.map((meeting, index) => (
            <Fragment key={meeting.id}>
              <Box
                onClick={() =>
                  (window.location.href = `/meetings/${meeting.id}`)
                }
                sx={{
                  position: "relative",
                  borderRadius: 2,
                  p: 2,
                  transition: "all 0.2s ease-in-out",
                  cursor: "pointer",
                  border: "1px solid",
                  borderColor: "transparent",
                  "&:hover": {
                    backgroundColor: "action.hover",
                    borderColor: "divider",
                  },
                  width: "100%",
                  boxSizing: "border-box",
                }}
              >
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      sm: "1fr 1fr",
                      lg: "1fr 1fr 1fr auto",
                    },
                    gap: 3,
                    alignItems: "start",
                    width: "100%",
                  }}
                >
                  {/* ORGANIZER SECTION */}
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      variant="caption"
                      fontWeight="bold"
                      color="text.secondary"
                      sx={{
                        display: "block",
                        mb: 0.5,
                        textTransform: "uppercase",
                      }}
                    >
                      Organizer
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight="600"
                      sx={{ wordBreak: "break-word" }}
                    >
                      {meeting.parentName}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        fontStyle: "italic",
                        display: "block",
                        wordBreak: "break-word",
                      }}
                    >
                      {meeting.parentEmail}
                    </Typography>
                  </Box>

                  {/* DESCRIPTION SECTION */}
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      variant="caption"
                      fontWeight="bold"
                      color="text.secondary"
                      sx={{
                        display: "block",
                        mb: 0.5,
                        textTransform: "uppercase",
                      }}
                    >
                      Description
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.primary"
                      sx={{ wordBreak: "break-word" }}
                    >
                      {meeting.description}
                    </Typography>
                  </Box>

                  {/* TIME & LOCATION SECTION */}
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      variant="caption"
                      fontWeight="bold"
                      color="text.secondary"
                      sx={{
                        display: "block",
                        mb: 0.5,
                        textTransform: "uppercase",
                      }}
                    >
                      Time & Location
                    </Typography>
                    <Typography variant="body2" fontWeight="500">
                      {formatDateTime(meeting.time)}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        fontStyle: "italic",
                        color: "text.secondary",
                        display: "block",
                        wordBreak: "break-word",
                      }}
                    >
                      {meeting.location}
                    </Typography>
                  </Box>

                  {/* ACTION BUTTON CONTAINER */}
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1}
                    sx={{
                      justifyContent: { xs: "flex-start", sm: "flex-end" },
                      alignSelf: "center",
                      // Keeps the buttons tight and prevents them from expanding vertically
                      width: { xs: "100%", sm: "auto" },
                      minWidth: { sm: "170px" },
                      mt: { xs: 1, lg: 0 },
                    }}
                  >
                    <Button
                      variant="contained"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAccept(meeting.id);
                      }}
                      size="small"
                      sx={{
                        py: 0.75, // Slimmer vertical padding
                        px: 2,
                        fontWeight: "600",
                        fontSize: "0.8125rem", // Crisp font size for a "small" button
                        borderRadius: 1,
                        flex: 1,
                      }}
                    >
                      Accept
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDecline(meeting.id);
                      }}
                      size="small"
                      sx={{
                        py: 0.75,
                        px: 2,
                        fontWeight: "600",
                        fontSize: "0.8125rem",
                        borderRadius: 1,
                        flex: 1,
                      }}
                    >
                      Decline
                    </Button>
                  </Stack>
                </Box>
              </Box>

              {index < meetings.length - 1 && <Divider sx={{ my: 1 }} />}
            </Fragment>
          ))}
        </Stack>
      ) : (
        <Typography
          variant="body2"
          sx={(theme) => ({
            textAlign: "center",
            fontStyle: "italic",
            color: theme.palette.paper.verylight,
            py: 2,
          })}
        >
          No pending meetings found.
        </Typography>
      )}
    </Box>
  );
}
